/**
 * "New customer" from the catalogue item page: creates the customer in CIS,
 * the same fields as the `customers/new` page without the market, and hands
 * it back so the page can order for it straight away (v1 completion C5,
 * mock-up state 8). No contact: contacts come with package D, and a card
 * order asks for one at checkout.
 */
import { useEffect, useId, useState } from "react";
import { Loader2 } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { CisCustomer, NewCustomerForm } from "@shared-core/data/api-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import {
  bindNewCustomerErrors,
  emptyNewCustomerDraft,
  fieldsNeedAFix,
  toCreateCisCustomerRequest,
  wontSeeNewCustomer,
  type NewCustomerDraft,
  type NewCustomerField,
} from "@shared-core/domain/new-customer";
import { useCanEditCustomers } from "../../auth/hooks";
import {
  NewCustomerFields,
  NotInPortfolioNotice,
} from "../domain/customers/NewCustomerFields";
import { Button } from "../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../primitives/dialog";

export function CreateCustomerDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (customer: CisCustomer) => void;
}) {
  const adapter = useDataAdapter();
  const canCreate = useCanEditCustomers();
  const idPrefix = useId();
  const [form, setForm] = useState<NewCustomerForm | null>(null);
  const [draft, setDraft] = useState<NewCustomerDraft>(() =>
    emptyNewCustomerDraft(null),
  );
  const [errors, setErrors] = useState<
    Partial<Record<NewCustomerField, string>>
  >({});
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    adapter
      .getNewCustomerForm()
      .then((loaded) => {
        if (cancelled) return;
        setForm(loaded);
        setDraft((current) => ({
          ...current,
          salesRepUserId: loaded.default_sales_rep_user_id ?? "",
          csrUserId: loaded.default_csr_user_id ?? "",
        }));
      })
      .catch(() => {
        // Without the lists the owners stay House Account; the create still works.
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, open]);

  const create = async () => {
    setSaving(true);
    setErrors({});
    setFailure(null);
    try {
      const created = await adapter.createCisCustomer(
        toCreateCisCustomerRequest(draft, { withMarket: false }),
      );
      onCreated(created);
    } catch (err) {
      const fieldErrors = extractFieldErrors(err);
      if (fieldErrors.length > 0) {
        const bound = bindNewCustomerErrors(fieldErrors);
        setErrors(bound.byField);
        setFailure([fieldsNeedAFix(bound.count), ...bound.unbound].join(" "));
      } else {
        setFailure(
          mutationErrorMessage(err, "The customer wasn't created. Try again."),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  if (!canCreate) return null;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!saving) onOpenChange(next);
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>New customer</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          {failure && (
            <p
              role="alert"
              className="rounded-md border border-red-300 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:text-red-400"
            >
              {failure}
            </p>
          )}
          {form && wontSeeNewCustomer(form, draft) && <NotInPortfolioNotice />}
          <NewCustomerFields
            idPrefix={idPrefix}
            form={form}
            draft={draft}
            onDraftChange={setDraft}
            errors={errors}
            disabled={saving}
            showMarket={false}
          />
          <p className="text-xs text-muted-foreground">
            Created in One. The customer is ready to order from straight away.
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="size-3.5 animate-spin" />}
              Create and use
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
